import FormElement from '@app/components/ui/FormElement';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { DatePicker, Empty, Spin, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAvailability, listItems } from '../../api';
import type { InventoryItem, RentalAvailability, RentalAvailabilityPoint } from '../../types';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_SPAN_DAYS = 366;

const AvailabilityTab: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useSmartErrorHandling();

  const [itemId, setItemId] = useState<number | null>(null);
  // Kept so the picker can render the chosen item's label without refetching.
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs(), dayjs().add(30, 'day')]);

  const [availability, setAvailability] = useState<RentalAvailability | null>(null);
  const [loading, setLoading] = useState(false);

  // The picker searches and pages server-side; only rentable items can be rented.
  const fetchRentableItems: ApiFetchFunction<InventoryItem> = useCallback(
    async (params: ApiQueryParams) => ({
      data: await listItems({ ...params, capability: 'is_rentable' }),
    }),
    []
  );

  const handleItemChange = useCallback((selection: InventoryItem | InventoryItem[] | undefined) => {
    const item = Array.isArray(selection) ? selection[0] : selection;
    setSelectedItem(item ?? null);
    setItemId(item?.itemId ?? null);
  }, []);

  const selectedItemsProvider = useCallback(() => selectedItem ?? undefined, [selectedItem]);

  const from = range[0];
  const to = range[1];

  // Fetch availability whenever a valid item + range is selected.
  useEffect(() => {
    if (itemId == null || !from || !to) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAvailability({ item_id: itemId, from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) })
      .then((data) => {
        if (!cancelled) setAvailability(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setAvailability(null);
          handleError(e as Error, { customMessage: t('inventory-availability-load-failed') });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, from, to, handleError, t]);

  const pointColumns = useMemo<ColumnsType<RentalAvailabilityPoint>>(
    () => [
      {
        title: t('inventory-availability-col-date'),
        dataIndex: 'date',
        key: 'date',
        render: (value: string) => dayjs(value).format('MMM DD, YYYY'),
      },
      {
        title: t('inventory-availability-col-free'),
        dataIndex: 'free',
        key: 'free',
        align: 'right',
        render: (value: number) => <span className="tabular-nums">{value}</span>,
      },
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <div style={{ minWidth: 260 }}>
          <FormElement label={t('inventory-availability-item-picker')}>
            <GenericSearchSelect<InventoryItem>
              className="w-full"
              allowClear
              value={itemId ?? undefined}
              apiFetch={fetchRentableItems}
              getItemId={(item) => item.itemId}
              getItemLabel={(item) => item.name}
              onSelectionChange={handleItemChange}
              selectedItemsProvider={selectedItemsProvider}
              placeholder={t('inventory-availability-item-picker')}
              searchPlaceholder={t('inventory-search-placeholder')}
            />
          </FormElement>
        </div>
        <div>
          <FormElement label={t('inventory-availability-range')}>
            <RangePicker
              value={range}
              allowClear={false}
              // Constrain the picked range to at most MAX_SPAN_DAYS from the first
              // chosen endpoint (Antd passes the in-progress selection in `info.from`).
              disabledDate={(current, info) => {
                if (!info.from) return false;
                return Math.abs(current.diff(info.from, 'day')) >= MAX_SPAN_DAYS;
              }}
              onChange={(values) => {
                if (values && values[0] && values[1]) setRange([values[0], values[1]]);
              }}
            />
          </FormElement>
        </div>
      </div>

      {itemId == null ? (
        <Empty description={t('inventory-availability-select-prompt')} />
      ) : (
        <Spin spinning={loading}>
          {availability ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-6 rounded-md bg-gray-50 p-4">
                <Statistic title={t('inventory-availability-owned')} value={availability.owned} />
                <Statistic title={t('inventory-availability-free-now')} value={availability.freeNow} />
                <Statistic
                  title={t('inventory-availability-next-free')}
                  value={
                    availability.nextFreeDate
                      ? dayjs(availability.nextFreeDate).format('MMM DD, YYYY')
                      : t('inventory-availability-next-free-none')
                  }
                />
              </div>
              <Table<RentalAvailabilityPoint>
                size="small"
                rowKey="date"
                columns={pointColumns}
                dataSource={availability.points}
                pagination={false}
                scroll={{ y: 360 }}
                locale={{ emptyText: <Typography>{t('inventory-availability-empty')}</Typography> }}
              />
            </div>
          ) : (
            !loading && <Empty description={t('inventory-availability-empty')} />
          )}
        </Spin>
      )}
    </div>
  );
};

export default AvailabilityTab;
